import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import {
  deriveFormulationSearch,
  isDispensable,
  scriptSureExhausted,
  type FormulationSearchResult,
} from './formulationSearch';
import type { MedicationSearchQueries, PrescribableMedication } from './types';

/**
 * The strength/form lookup behind the medication picker's second step, in three stages.
 *
 * 1. ScriptSure's `$drug-search` drill-down by `routedMedId`.
 * 2. The same drill-down carrying the drug row's `gcnSeqno`. Devices, supplies, OTC and topical
 *    products have no dose-level rows, so stage 1 returns nothing for them; the vendor resolves a
 *    marketed formulation from the formulation key instead. Only worth a request when the drug row
 *    carried a key and stage 1 came back empty.
 * 3. The RxNorm catalog (`searchFallbackFormulations`), for drugs ScriptSure lists no strengths for
 *    because they have no saved formats (medplum-tracker#17) — `$order-medication` accepts any
 *    current product-level RxNorm code, so those rows are still prescribable.
 *
 * Each stage runs only when the one before it settles empty, so the common path costs a single
 * request. The settled/loading logic lives in `formulationSearch.ts` so its edge cases are
 * unit-testable without react-query.
 */
export function useFormulationSearch(
  drug: PrescribableMedication | null,
  queries: MedicationSearchQueries,
): FormulationSearchResult {
  const routedMedId = drug?.routedMedId;
  const scriptSureQuery = useQuery({
    ...queries.searchMedications({ routedMedId: routedMedId ?? '' }),
    enabled: Boolean(routedMedId),
  });
  const scriptSureFormulations = useMemo(
    () => ((scriptSureQuery.data ?? []) as PrescribableMedication[]).filter(isDispensable),
    [scriptSureQuery.data],
  );

  const gcnSeqno = drug?.gcnSeqno;
  const gcnQuery = useQuery({
    // The key is omitted rather than blanked when there is none: the routers validate it as
    // digits-only, so an empty string is a present-and-invalid value, not an absent one.
    ...queries.searchMedications({
      routedMedId: routedMedId ?? '',
      ...(gcnSeqno ? { gcnSeqno } : {}),
    }),
    enabled:
      Boolean(routedMedId) && Boolean(gcnSeqno) && scriptSureExhausted(scriptSureQuery, scriptSureFormulations.length),
  });
  const gcnFormulations = useMemo(
    () => ((gcnQuery.data ?? []) as PrescribableMedication[]).filter(isDispensable),
    [gcnQuery.data],
  );

  // Whether stage 2 spoke for the vendor. A key is not device-specific — ScriptSure stamps one on
  // ordinary drug rows too — so the question is whether stage 1 came up empty and the retry
  // actually ran, not whether a key exists. Reading stage 2's state when it never ran would leave
  // the lookup permanently unresolved.
  const retried = Boolean(gcnSeqno) && scriptSureExhausted(scriptSureQuery, scriptSureFormulations.length);

  // The formulation key is the vendor's own answer for a drug with no dose-level rows, so exhaust
  // it before reaching for a different catalog.
  const vendorExhausted = retried
    ? scriptSureExhausted(gcnQuery, gcnFormulations.length)
    : scriptSureExhausted(scriptSureQuery, scriptSureFormulations.length);

  const fallbackQuery = useQuery({
    ...queries.searchFallbackFormulations({ drugName: drug?.name ?? '' }),
    enabled: Boolean(drug) && vendorExhausted,
  });
  const fallbackFormulations = useMemo(
    () => ((fallbackQuery.data ?? []) as PrescribableMedication[]).filter(isDispensable),
    [fallbackQuery.data],
  );

  const vendorFormulations = scriptSureFormulations.length > 0 ? scriptSureFormulations : gcnFormulations;

  return deriveFormulationSearch({
    scriptSure: {
      isFetching: scriptSureQuery.isFetching || gcnQuery.isFetching,
      isSuccess: retried ? gcnQuery.isSuccess : scriptSureQuery.isSuccess,
      isError: retried ? gcnQuery.isError : scriptSureQuery.isError,
    },
    fallback: fallbackQuery,
    scriptSureFormulations: vendorFormulations,
    fallbackFormulations,
  });
}
